import { exportAll, importAll, metaSet } from './db.js';
import { ensurePhase2Migration } from './migrations.js';
import { nowIso } from '../core/time.js';

export type BackupFormatVersion = 1 | 2;

export interface BackupEnvelope {
  app: 'wean';
  formatVersion: BackupFormatVersion;
  schemaVersion: number;
  createdAt: string;
  data: Record<string, unknown[]>;
}

export async function buildBackup(): Promise<BackupEnvelope> {
  const data = await exportAll();
  const env: BackupEnvelope = {
    app: 'wean',
    formatVersion: 2,
    // Internal SCHEMA_VERSION (events/payloads). Bump alongside core changes;
    // restorer migrates older v1 backups before importing.
    schemaVersion: 3,
    createdAt: nowIso(),
    data,
  };
  return env;
}

export async function downloadBackup(): Promise<void> {
  const env = await buildBackup();
  const blob = new Blob([JSON.stringify(env, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  a.href = url; a.download = `wean-backup-${ts}.json`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  await metaSet('last_backup_at', nowIso());
}

export function parseBackup(text: string): BackupEnvelope {
  const env = JSON.parse(text) as BackupEnvelope;
  if (env.app !== 'wean') throw new Error('Not a Wean backup file.');
  if (env.formatVersion !== 1 && env.formatVersion !== 2) throw new Error('Unsupported backup format.');
  if (!env.data || typeof env.data !== 'object') throw new Error('Backup data missing.');
  return env;
}

export async function restoreBackup(env: BackupEnvelope, mode: 'replace' | 'merge'): Promise<void> {
  await importAll(env.data, mode);
  // Always re-run migration after import. v1 backups predate the Phase-2
  // stores; v2 backups can also originate from an older SCHEMA_VERSION where
  // some habits never got default consumption methods seeded. Clearing the
  // marker ensures freshly imported habits are backfilled regardless of
  // source schema.
  await metaSet('phase2_methods_migrated_at', undefined);
  await ensurePhase2Migration();
}
